/**
 * MilestoneStrip — compact milestone governance timeline.
 * Shows top 12 milestones as a horizontal strip with status chips.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { CockpitMilestone, MilestoneStatus } from '@/types/cockpit';

export interface MilestoneStripProps {
  milestones: CockpitMilestone[];
  className?: string;
}

const STATUS_VARIANT: Record<MilestoneStatus, 'ok' | 'warn' | 'critical' | 'outline' | 'default'> = {
  'on-track': 'ok',
  upcoming: 'warn',
  late: 'critical',
  overdue: 'critical',
  complete: 'ok',
  na: 'outline',
};

const STATUS_LABEL: Record<MilestoneStatus, string> = {
  'on-track': 'On Track',
  upcoming: 'Upcoming',
  late: 'Late',
  overdue: 'Overdue',
  complete: 'Done',
  na: 'N/A',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  try {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return d;
  }
}

function truncateName(name: string, max = 28): string {
  if (name.length <= max) return name;
  return name.slice(0, max - 1) + '…';
}

export const MilestoneStrip: React.FC<MilestoneStripProps> = ({ milestones, className }) => {
  const navigate = useNavigate();

  if (milestones.length === 0) {
    return (
      <div className={cn('rounded-lg border bg-card p-4 text-center', className)}>
        <p className="text-sm text-muted-foreground">
          No milestones found. Import a schedule with milestone activities.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card', className)}>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left font-medium px-3 py-2">Milestone</th>
              <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Forecast</th>
              <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Baseline</th>
              <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Variance</th>
              <th className="text-center font-medium px-3 py-2">Status</th>
              <th className="text-center font-medium px-3 py-2">Logic</th>
            </tr>
          </thead>
          <tbody>
            {milestones.map((ms) => (
              <tr
                key={ms.id}
                className="border-b last:border-b-0 hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={() =>
                  navigate(`/app/analytics/milestones?milestone_id=${ms.id}`)
                }
              >
                <td className="px-3 py-1.5 font-medium text-foreground max-w-[200px] truncate" title={ms.name}>
                  {truncateName(ms.name)}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">
                  {formatDate(ms.forecastDate)}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">
                  {formatDate(ms.baselineDate)}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {ms.varianceDays !== null ? (
                    <span
                      className={cn(
                        'font-medium',
                        ms.varianceDays > 0
                          ? 'text-red-500'
                          : ms.varianceDays < 0
                            ? 'text-emerald-500'
                            : 'text-muted-foreground',
                      )}
                    >
                      {ms.varianceDays > 0 ? '+' : ''}
                      {ms.varianceDays}d
                    </span>
                  ) : (
                    <span className="text-muted-foreground">N/A</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <Badge variant={STATUS_VARIANT[ms.status]} size="sm">
                    {STATUS_LABEL[ms.status]}
                  </Badge>
                </td>
                <td className="px-3 py-1.5 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {ms.noPred && (
                      <span className="text-[10px] text-amber-600 dark:text-amber-400" title="No predecessor">
                        No Predecessor
                      </span>
                    )}
                    {ms.noSucc && (
                      <span className="text-[10px] text-amber-600 dark:text-amber-400" title="No successor">
                        No Successor
                      </span>
                    )}
                    {!ms.noPred && !ms.noSucc && (
                      <span className="text-[10px] text-emerald-500">✓</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
